import ClaimButton, { ClaimButtonRef } from "@components/inputs/ClaimButton";
import { toast } from "@components/utils/Toaster";
import { useMegaShareStatus } from "@hooks/useMegaShareStatus";
import useThrottledCallback from "@hooks/useThrottledCallback";
import { Countdown } from "@views/PromotionMegaSharePage/components";
import type { CountdownRef } from "@views/PromotionMegaSharePage/components/Countdown/Countdown";
import { DualClaimableCats } from "@views/PromotionMegaSharePage/components/GiverContent/components";
import { DualClaimableCatsRef } from "@views/PromotionMegaSharePage/components/GiverContent/components/DualClaimableCats/DualClaimableCats";
import ShineCircleBg from "@views/PromotionMegaSharePage/components/ShineCircleBg";
import { MegaShareState } from "@views/PromotionMegaSharePage/types";
import { MegaShareActor } from "@views/PromotionMegaSharePage/types/mega-share-state";
import { delay, Dialog, PageTitle, useCustomEventsListener } from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EVENT_CLOSE_MEGA_SHARE_FRIEND_DIALOG, EVENT_OPEN_MEGA_SHARE_FRIEND_DIALOG } from "./constants";

const DELAY_CLOSE_DIALOG_MS = 2000;

const MegaShareFriendDialog = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { taker, details, refetch: refetchMegaShareStatus, claimReward, isClaiming } = useMegaShareStatus();
  const isClaimable = taker?.status === MegaShareState.CLAIMABLE;
  const claimAmount = details?.takerClaimAmount;
  const remainingSeconds = details?.remainingSeconds;
  const refClaimButton = useRef<ClaimButtonRef>(null);
  const refCountdown = useRef<CountdownRef>(null);
  const refDualClaimableCats = useRef<DualClaimableCatsRef>(null);
  const [isClaimed, setIsClaimed] = useState(false);
  const [preservedClaimAmount, setPreservedClaimAmount] = useState<number | undefined>(undefined);
  const hasClaimedRef = useRef(false);

  // Keep showing claimable state even after refetch changes status during claim flow
  const shouldShowClaimable = isClaimable || isClaimed;

  // Use preserved claim amount when claimed, otherwise use current claimAmount
  const displayClaimAmount = isClaimed && preservedClaimAmount !== undefined ? preservedClaimAmount : claimAmount ?? 0;

  // Recalculate endTime only when remainingSeconds actually changes (after refetch completes)
  // This ensures countdown shows correct time when dialog reopens
  const endTime = useMemo(() => {
    if (!open || (remainingSeconds ?? 0) <= 0 || isClaimed) {
      return undefined;
    }
    return moment().add(remainingSeconds ?? 0, "seconds");
  }, [open, remainingSeconds, isClaimed]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    hasClaimedRef.current = false;
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setIsClaimed(false);
  }, []);

  // Refetch data when dialog opens to ensure remainingSeconds is up-to-date
  useEffect(() => {
    if (open) {
      refetchMegaShareStatus();
    }
  }, [open, refetchMegaShareStatus]);

  // Auto-close dialog when WS refetch makes taker no longer claimable
  useEffect(() => {
    if (open && !shouldShowClaimable && !isClaimed) {
      toast.warning(t("sorry this bonus is no longer available"), {
        id: "mega-share-taker-unavailable-toast",
      });
      handleClose();
    }
  }, [open, shouldShowClaimable, isClaimed, t, handleClose]);

  // Handle case where dialog opens but bonus has already expired (remainingSeconds <= 0)
  useEffect(() => {
    if (open && !isClaimed && (remainingSeconds ?? 0) <= 0) {
      toast.warning(t("sorry this bonus is no longer available"), {
        id: "mega-share-taker-unavailable-toast",
      });
      refetchMegaShareStatus();
      handleClose();
    }
  }, [open, isClaimed, remainingSeconds, t, refetchMegaShareStatus, handleClose]);

  const handleCountdownComplete = useCallback(() => {
    // Don't trigger completion if already claimed
    if (hasClaimedRef.current) {
      return;
    }
    toast.warning(t("sorry this bonus is no longer available"));
    refetchMegaShareStatus();
    handleClose();
  }, [t, refetchMegaShareStatus, handleClose]);

  const handleClaimThrottled = useThrottledCallback(async () => {
    const isSuccess = await claimReward(MegaShareActor.FRIEND);
    if (!isSuccess) {
      return;
    }
    setIsClaimed(true);
    hasClaimedRef.current = true;
    // Stop countdown to prevent repeated onComplete calls
    refCountdown.current?.stop();
    // Preserve claim amount before refetch changes it
    setPreservedClaimAmount(claimAmount);
    refClaimButton.current?.renderFlyingCoins();
    refDualClaimableCats.current?.startClaimAnimation();
    delay(DELAY_CLOSE_DIALOG_MS).then(() => {
      handleClose();
      // Refetch after dialog closes to update status
      refetchMegaShareStatus();
    });
  }, 300);

  // handle open/close event
  useCustomEventsListener({
    [EVENT_OPEN_MEGA_SHARE_FRIEND_DIALOG]: handleOpen,
    [EVENT_CLOSE_MEGA_SHARE_FRIEND_DIALOG]: handleClose,
  });

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      title={
        <PageTitle
          label={t("you got a gift")}
          variant="gradient"
          classes={{
            "label&": "w-full text-center line-clamp-2 !relative",
            "labelOverlay&": "w-full text-center line-clamp-2",
            "labelContainer&": "h-auto w-[calc(100%-16px)] mx-auto text-center",
          }}
        />
      }
      className=""
      classes={{
        "container&":
          "z-[50] !h-[484px] overflow-hidden !px-0 !bg-grdLow  rounded-[24px] border border-stroke-base shadow-none",
        "containerWrapper&": "!z-[9999]",
        "backDrop&": "!z-[9998]",
      }}
      transition
    >
      {/* CLAIMABLE STATE */}
      {shouldShowClaimable && (
        <>
          {/* SHINE CIRCLE BG */}
          <div className="translate-y-[-60px] scale-[1.3]">
            <ShineCircleBg className="max-h-[165px]" />
          </div>

          {/* DUAL CLAIMABLE CATS */}
          <div className="-mx-5 flex translate-y-[-44px] justify-center overflow-hidden">
            <DualClaimableCats
              subTitle={t("you got a gift from")}
              title={t("your friend")}
              prizeAmount={displayClaimAmount}
              ref={refDualClaimableCats}
            />
          </div>

          {/* BODY*/}
          <div className="translate-y-[-140px] px-5">
            {/* CLAIM BUTTON */}
            <ClaimButton
              onClick={handleClaimThrottled}
              withCoin={!isClaimed}
              withShine={!isClaimed}
              claimable={!isClaimed}
              text={t("Claim Now")}
              loading={isClaiming}
              wrapperClassName="mt-3"
              classes={{
                text: "text-base",
              }}
              withFlyingCoins
              aria-label={"Claim Mega Share Bonus"}
              size="lg"
              ref={refClaimButton}
            />

            {/* GIVER INFO */}
            {open && (
              <div className="mt-6">
                <Countdown
                  ref={refCountdown}
                  endTime={endTime}
                  title={t("claim now before its gone")}
                  autoStart
                  classNameTitle="tracking-normal normal-case !text-base !font-normal"
                  onComplete={handleCountdownComplete}
                />
              </div>
            )}
          </div>
        </>
      )}
    </Dialog>
  );
};

export default MegaShareFriendDialog;
